import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Typography,
  InputAdornment,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Phone,
  Search,
  CallMade,
  CallReceived,
  Bookmark,
  FilterList
} from '@mui/icons-material';
import { useApi } from '../../contexts/ApiContext';
import { format } from 'date-fns';

/**
 * Tab de Historial Completo - Solo consulta, no duplica datos
 * Permite re-llamar desde el historial
 */
const CallHistoryTab = ({ agentId, onRedialNumber }) => {
  const { callHistory } = useApi();

  const [historyCalls, setHistoryCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCalls, setTotalCalls] = useState(0);

  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    direction: 'all',
    skill: '',
    inTracking: 'all'
  });

  // Cargar historial
  const loadHistory = async () => {
    try {
      setLoading(true);

      console.log('[CALL HISTORY] Cargando historial para agente:', agentId);

      const params = {
        page: page + 1,
        limit: rowsPerPage
      };

      if (filters.search) params.search = filters.search;
      if (filters.direction !== 'all') params.direction = filters.direction;
      if (filters.skill) params.skill = filters.skill;
      if (filters.inTracking !== 'all') params.inTracking = filters.inTracking === 'yes';

      console.log('[CALL HISTORY] Parámetros de consulta:', params);

      const response = await callHistory.getAgentHistory(agentId, params);
      console.log('[CALL HISTORY] Respuesta del servidor:', response);

      if (response.success) {
        setHistoryCalls(response.data || []);
        setTotalCalls(response.pagination?.total || 0);
      }
    } catch (error) {
      console.error('[CALL HISTORY] Error cargando historial:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (agentId) {
      loadHistory();
    }
  }, [agentId, page, rowsPerPage, filters]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0); // Reset to first page when filtering
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Filtros */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Búsqueda por número */}
          <TextField
            size="small"
            placeholder="Buscar por número..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 200 }}
          />

          {/* Filtro por dirección */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Dirección</InputLabel>
            <Select
              value={filters.direction}
              label="Dirección"
              onChange={(e) => handleFilterChange('direction', e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="inbound">Entrantes</MenuItem>
              <MenuItem value="outbound">Salientes</MenuItem>
            </Select>
          </FormControl>

          {/* Filtro por seguimiento */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>En seguimiento</InputLabel>
            <Select
              value={filters.inTracking}
              label="En seguimiento"
              onChange={(e) => handleFilterChange('inTracking', e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="yes">Sí</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          {/* Indicador de filtros activos */}
          {(filters.search || filters.direction !== 'all' || filters.inTracking !== 'all') && (
            <Chip
              icon={<FilterList />}
              label="Filtros activos"
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {/* Tabla */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
          </Box>
        ) : historyCalls.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No se encontraron llamadas
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Número</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Dirección</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Duración</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Skill</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Tipificación</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }} align="center">Seguimiento</TableCell>
                  {onRedialNumber && (
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }} align="right">Acciones</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {historyCalls.map((call) => (
                  <TableRow
                    key={call.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {format(new Date(call.call_date), 'dd/MM HH:mm')}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {call.phone_number}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {call.call_direction === 'inbound' ? (
                        <Chip
                          icon={<CallReceived />}
                          label="Entrante"
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 24, fontSize: '0.7rem' }}
                        />
                      ) : (
                        <Chip
                          icon={<CallMade />}
                          label="Saliente"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ height: 24, fontSize: '0.7rem' }}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {formatDuration(call.call_duration)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {call.skill || '-'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Tooltip title={call.typification_path || ''}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.8rem',
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {call.typification || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      {call.in_tracking && (
                        <Tooltip title={`Etiquetas: ${call.tracking_tags?.join(', ') || 'Sin etiquetas'}`}>
                          <Chip
                            icon={<Bookmark />}
                            label="Sí"
                            size="small"
                            color="primary"
                            sx={{ height: 24, fontSize: '0.7rem' }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>

                    {onRedialNumber && (
                    <TableCell align="right">
                      <Tooltip title="Rellamar">
                        <IconButton
                          size="small"
                          onClick={() => onRedialNumber(call)}
                          color="primary"
                        >
                          <Phone fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Paginación */}
      {!loading && historyCalls.length > 0 && (
        <TablePagination
          component="div"
          count={totalCalls}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{ borderTop: '1px solid #e0e0e0' }}
        />
      )}
    </Box>
  );
};

export default CallHistoryTab;
